import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { fetchToursPopularDestinations } from 'actions/GlobalSearch/GlobalSearchToursActions'
import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import Places from 'constants/places'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferList from 'hooks/Offers/useOfferList'
import { isCruiseOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { findCheapestOfTourV2Offer } from 'lib/tours/tourUtils'
import { getToursPopularDestinations } from 'selectors/destinationSelectors'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import LoyaltySpendPointsCarouselSection from './LoyaltySpendPointsCarouselSection'
import LoyaltySpendPointsOfferFilterPrefetch from './LoyaltySpendPointsOfferFilterPrefetch'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { MAX_CAROUSEL_FILTER_OPTIONS, MINIMUM_OFFERS_TO_SHOW_CAROUSEL, SPEND_POINTS_CAROUSEL_OFFER_LIMIT } from './loyaltySpendPointsConstants'
import useLoyaltySpendPointsFilterAvailability from './useLoyaltySpendPointsFilterAvailability'
import useLoyaltySpendPointsOfferListBurnSort from './useLoyaltySpendPointsOfferListBurnSort'

const ALL_DESTINATIONS_VALUE = ''

interface Props {
  title: ReactNode
  includeCruises?: boolean
  sortOption: SpendPointsSortOption
}

function LoyaltySpendPointsTourCarousel(props: Props) {
  const { title, includeCruises = false, sortOption } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const { showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const popularDestinations = useAppSelector(getToursPopularDestinations)
  const [selectedDestination, setSelectedDestination] = useState(ALL_DESTINATIONS_VALUE)
  const [cashPriceCap, setCashPriceCap] = useState<number>()

  useEffect(() => {
    dispatch(fetchToursPopularDestinations())
  }, [dispatch])

  const destinationOptions = useMemo(() => [
    {
      value: ALL_DESTINATIONS_VALUE,
      label: intl.formatMessage({
        id: 'cHfgYcUKNwHIOx1oDt2s',
        defaultMessage: 'Around the world',
        description: 'Filter option label for tours available around the world',
      }),
    },
    ...popularDestinations
      .filter(destination => destination.value !== Places.Anywhere.id)
      .slice(0, MAX_CAROUSEL_FILTER_OPTIONS - 1)
      .map(destination => ({
        value: destination.value,
        label: destination.format.mainText,
      })),
  ], [intl, popularDestinations])

  const onDestinationChange = useCallback((destination: string) => {
    setSelectedDestination(currentDestination => currentDestination === destination ? ALL_DESTINATIONS_VALUE : destination)
  }, [])

  useEffect(() => {
    setCashPriceCap(undefined)
  }, [showOffersWithinBalance])

  const sortByOption: Record<SpendPointsSortOption, App.OfferListSortByOptions | undefined> = {
    // Combined tours+cruises must use 'vertical-tour' to route through the unified search
    // endpoint (see isUnifiedSpecificRequest in offer.ts). Price-based server sort isn't
    // available; client-side points sort is applied by useLoyaltySpendPointsOfferListBurnSort.
    recommended: includeCruises ? 'vertical-tour' : undefined,
    'lowest-points': includeCruises ? 'vertical-tour' : 'price.asc',
    'highest-points': includeCruises ? 'vertical-tour' : 'price.desc',
  }
  const sortBy = sortByOption[sortOption]
  const offerListFilters = useMemo<App.OfferListFilters>(() => ({
    // The regional Tours & Cruises page uses unified inventory for its combined list.
    offerTypes: includeCruises ? [OFFER_TYPE_TOUR_V2, OFFER_TYPE_TOUR, OFFER_TYPE_CRUISE] : [OFFER_TYPE_TOUR_V2],
    destinationId: includeCruises ? undefined : Places.Anywhere.id,
    destinationIds: includeCruises
      ? (selectedDestination ? selectedDestination.split(',') : [Places.Anywhere.id])
      : undefined,
    destinations: !includeCruises && selectedDestination ? selectedDestination.split(',') : undefined,
    limit: SPEND_POINTS_CAROUSEL_OFFER_LIMIT,
    sortBy,
    viewPriceInPoints: true,
    priceLte: showOffersWithinBalance && cashPriceCap !== undefined
      ? String(cashPriceCap)
      : undefined,
  }), [cashPriceCap, includeCruises, selectedDestination, showOffersWithinBalance, sortBy])
  const rawOfferList = useOfferList(offerListFilters)
  const offerList = useMemo(() => includeCruises ? rawOfferList : {
    ...rawOfferList,
    offerIds: rawOfferList.offerIds.filter(id => !id.startsWith('cruise:')),
  }, [includeCruises, rawOfferList])
  const getBurnRequest = useCallback((offer: App.AnyOffer) => {
    if (isCruiseOffer(offer)) {
      const price = offer.mainDepartureDetails.lowestOverallPriceDetails?.total
      const loyaltyProductType = getLoyaltyProductType(offer)
      return price && loyaltyProductType ? { standard: { price, loyaltyProductType } } : undefined
    }

    const loyaltyProductType = getLoyaltyProductType(offer)
    if (!isTourV2Offer(offer)) return undefined

    const cheapestOption = findCheapestOfTourV2Offer(offer, undefined, isLuxPlusToursEnabled).cheapestPurchasableOption
    const price = cheapestOption?.price
    if (!price || !loyaltyProductType) return undefined

    const memberPrice = cheapestOption.memberPrice
    return {
      standard: { price, loyaltyProductType },
      member: memberPrice ? { price: memberPrice, loyaltyProductType } : undefined,
    }
  }, [isLuxPlusToursEnabled])
  const sortedOfferList = useLoyaltySpendPointsOfferListBurnSort({
    offerList,
    sortOption,
    getBurnRequest,
  })

  useEffect(() => {
    if (sortedOfferList.cashPriceCap !== undefined) {
      setCashPriceCap(currentCap => Math.max(currentCap ?? 0, sortedOfferList.cashPriceCap!))
    }
  }, [sortedOfferList.cashPriceCap])

  useEffect(() => {
    if (
      showOffersWithinBalance &&
      selectedDestination !== ALL_DESTINATIONS_VALUE &&
      !sortedOfferList.offerList.fetching &&
      sortedOfferList.offerList.offerIds.length === 0
    ) {
      setSelectedDestination(ALL_DESTINATIONS_VALUE)
    }
  }, [showOffersWithinBalance, selectedDestination, sortedOfferList.offerList.fetching, sortedOfferList.offerList.offerIds.length])

  const { visibleOptions: visibleDestinationOptions, onAvailabilityChange: onDestinationAvailabilityChange, isResolved: areDestinationFiltersResolved, shouldShowFilters } = useLoyaltySpendPointsFilterAvailability({
    options: destinationOptions,
    selectedValue: selectedDestination,
    defaultValue: ALL_DESTINATIONS_VALUE,
    enabled: showOffersWithinBalance,
    availabilityKey: cashPriceCap?.toString(),
    onSelectedValueChange: setSelectedDestination,
  })

  const destinationPrefetchFilters = useMemo(() => (
    destinationOptions
      .filter(option => !!option.value)
      .map(option => ({
        value: option.value,
        filters: {
          ...offerListFilters,
          destinationIds: includeCruises ? option.value.split(',') : undefined,
          destinations: includeCruises ? undefined : option.value.split(','),
          limit: MINIMUM_OFFERS_TO_SHOW_CAROUSEL,
        },
      }))
  ), [destinationOptions, includeCruises, offerListFilters])

  if (showOffersWithinBalance && !selectedDestination && !sortedOfferList.offerList.fetching && sortedOfferList.offerList.offerIds.length < MINIMUM_OFFERS_TO_SHOW_CAROUSEL) {
    return null
  }

  return <>
    {showOffersWithinBalance && cashPriceCap !== undefined && destinationPrefetchFilters.map(({ value, filters }) => <LoyaltySpendPointsOfferFilterPrefetch
      key={value}
      value={value}
      filters={filters}
      onAvailabilityChange={onDestinationAvailabilityChange}
    />)}
    <LoyaltySpendPointsCarouselSection
      title={title}
      viewAllTo="/tours"
      filters={offerListFilters}
      offerList={sortedOfferList.offerList}
      filterOptions={visibleDestinationOptions}
      selectedFilter={selectedDestination}
      onFilterChange={onDestinationChange}
      showFilters={shouldShowFilters}
      filtersLoading={showOffersWithinBalance && shouldShowFilters && !areDestinationFiltersResolved}
      testId="loyalty-spend-points-tours-carousel"
    />
  </>
}

export default LoyaltySpendPointsTourCarousel
